import React, { useState, useEffect, useRef } from "react";
import { CloseIcon } from "../components";

const SearchBar = ({
  className,
  inputClassName,
  inputName,
  inputPlaceholder,
  items = [],
  onSearch
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  //  Ensure `items` is always an array
  const safeItems = Array.isArray(items) ? items : [];

  //  Filter suggestions
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSuggestions([]);
      onSearch?.("");
      return;
    }

    const filteredSuggestions = safeItems.filter((item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    console.log("SafeItems:", safeItems);
    

    setSuggestions(filteredSuggestions);
    setHighlightIndex(-1); // Reset highlight index
  }, [searchTerm, safeItems , onSearch]);

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedItem(null);
    onSearch?.("");
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setSelectedItem(suggestion);
    setSuggestions([]);
    if(onSearch) onSearch(suggestion?.name);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    setSuggestions([]);
    setSelectedItem(null);
    onSearch?.("");
  };

  //Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
   const query = selectedItem ? selectedItem.name : searchTerm;
   if(onSearch) onSearch?.(query);
    setSuggestions([]);
  };

  // Keyboard navigation (Arrow Up/Down & Enter)
  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      setHighlightIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[highlightIndex]);
    }
  };

  return (
    <div className={`flex-auto text-end ${className}`}>
      <div className="relative">
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            className={`w-full py-2 pr-8 truncate text-base leading-5.5 bg-no-repeat border border-gray-100 rounded-lg bg-search-icon-gray-500 pl-9 bg-left-12 bg-16 md:max-w-80 focus:outline-none focus:border-gold-200 focus:bg-search-icon-gold-200 ${inputClassName}`}
            placeholder={inputPlaceholder || "Search by name"}
            type="text"
            name={inputName || "s"}
            value={searchTerm} // Bind value to state
            onChange={handleInputChange} //  Ensure change is handled
            onKeyDown={handleKeyDown}
            autoComplete="off"
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls="search-suggestions"
          />
          {searchTerm && (
            <button
              type="button"
              className="absolute right-3 top-0 bottom-0 text-gray-500 hover:text-gold-200"
              onClick={handleClearSearch}
              aria-label="Clear search"
            >
              <span className="h-6 w-6">
                <CloseIcon className="h-4 w-4" />
              </span>
            </button>
          )}
          {suggestions.length > 0 && (
            <ul
              ref={suggestionsRef}
              id="search-suggestions"
              className="absolute bg-gray-0 rounded-b-lg mt-1 w-full overflow-y-auto shadow-search-sugg z-10 p-2 text-left md:max-w-80 right-0"
              role="listbox"
              aria-live="polite"
            >
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.id}
                  className={`px-2 py-1 text-base leading-5.5 cursor-pointer text-gray-900 hover:text-gold-200 hover:bg-gold-200 hover:bg-opacity-10 mb-1 last:mb-0 ${
                    index === highlightIndex ? "bg-gold-100" : ""
                  }`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={() => setHighlightIndex(index)}
                  role="option"
                  aria-selected={index === highlightIndex}
                >
                  {suggestion.name
                    .split(new RegExp(`(${searchTerm})`, "i"))
                    .map((part, i) =>
                      part.toLowerCase() === searchTerm.toLowerCase() ? (
                        <span
                          key={i}
                          className="text-gold-200 font-normal text-base leading-5.5"
                        >
                          {part}
                        </span>
                      ) : (
                        <span
                          key={i}
                          className="font-normal text-base leading-5.5 text-gray-900"
                        >
                          {part}
                        </span>
                      )
                    )}
                </li>
              ))}
            </ul>
          )}
        </form>
      </div>
    </div>
  );
};

export default SearchBar;
